/**
 * The Team queue's Status filter, read from and written to `?status=`: which
 * groups print, how many tickets each status holds under the search (the
 * menu's counts), whether Archived prints, and the dashed line a chosen
 * status prints when it holds nothing. None chosen is the queue as it is.
 */
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { QueueGroupRows } from "../ticket/queueGroups.js";
import {
  STATUS_FILTER_LABEL,
  parseStatusParam,
  statusParamOf,
  type StatusFilter,
} from "../ticket/statusFilter.js";
import type { PipelineRow } from "../work/workViews.js";

const STATUS_EMPTY_LINE: Record<Exclude<StatusFilter, "needs">, string> = {
  ready: "Nothing is ready to merge.",
  live: "Nothing is being worked on right now.",
  stop: "Nothing is stopped.",
  done: "Nothing has finished yet.",
  archived: "Nothing is archived.",
};

/** A chosen status's dashed line when it holds nothing: its own, or what the search did not find. */
function statusEmptyLine(status: Exclude<StatusFilter, "needs">, searching: boolean): string {
  return searching
    ? `Nothing in ${STATUS_FILTER_LABEL[status]} matches.`
    : STATUS_EMPTY_LINE[status];
}

export interface StatusFilterView {
  /** The raw `?status=`, for effects that start over when it changes. */
  param: string | null;
  statuses: StatusFilter[];
  filtering: boolean;
  /** Tickets each status holds under the search. */
  counts: Record<StatusFilter, number>;
  /** The groups that print: the chosen ones, or every group. */
  shownGroups: QueueGroupRows[];
  showArchived: boolean;
  /** Tickets printed across the shown groups and Archived. */
  shownCount: number;
  setStatuses: (chosen: StatusFilter[]) => void;
  /** The dashed line a chosen, empty status prints; undefined while nothing is chosen. */
  emptyLine: (status: Exclude<StatusFilter, "needs">) => string | undefined;
}

export function useStatusFilter({
  shown,
  archived,
  searching,
}: {
  /** The queue's groups, already narrowed by the search. */
  shown: QueueGroupRows[];
  /** The Team's archived rows, already narrowed by the search. */
  archived: PipelineRow[];
  searching: boolean;
}): StatusFilterView {
  const [params, setParams] = useSearchParams();
  const param = params.get("status");
  const statuses = useMemo(() => parseStatusParam(param), [param]);
  const filtering = statuses.length > 0;
  const counts = useMemo(() => {
    const out = { archived: archived.length } as Record<StatusFilter, number>;
    for (const { group, rows } of shown) out[group] = rows.length;
    return out;
  }, [shown, archived]);
  const shownGroups = filtering ? shown.filter(({ group }) => statuses.includes(group)) : shown;
  const showArchived = filtering ? statuses.includes("archived") : searching;
  const shownCount =
    shownGroups.reduce((sum, { rows }) => sum + rows.length, 0) +
    (showArchived ? archived.length : 0);
  const setStatuses = (chosen: StatusFilter[]) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const value = statusParamOf(chosen);
        if (value) next.set("status", value);
        else next.delete("status");
        next.delete("done");
        return next;
      },
      { replace: true },
    );
  return {
    param,
    statuses,
    filtering,
    counts,
    shownGroups,
    showArchived,
    shownCount,
    setStatuses,
    emptyLine: (status) => (filtering ? statusEmptyLine(status, searching) : undefined),
  };
}
